import type {
  ComponentView,
  ErpDocumentAction,
  ErpDocumentKind,
  ErpDocumentMode,
  ErpDocumentState,
  ErpReadinessId,
  ErpReadinessState,
  ErpReadinessStep,
  ErpConnectionView,
  ErpConnectionStatus,
  ErpIssueSeverity,
  ErpMappingState,
  ErpPriceSource,
  ErpStockQuantity,
  ErpSupport,
  StoreErpSettings,
  StoreInstallationView,
  StoreWebsiteView,
} from '../../api';
import type { Level } from '../EnvironmentReadiness';
import { magentoStoreCopy, type StoreCopy } from './storeCopy';

/**
 * The words the ERP integrations screens use (CO-77).
 *
 * The API speaks in stable identifiers (`auth_failed`, `no_code`,
 * `price_change_held`); those are contracts, not copy. Everything a person
 * reads goes through here. See docs/UX_TERMINOLOGY.md, "ERP integrations".
 */

export const connectionStatusLabel: Record<ErpConnectionStatus, string> = {
  draft: 'Not connected yet',
  connected: 'Connected',
  auth_failed: 'Access key refused',
  unavailable: 'Not reachable',
};

export const connectionStatusLevel: Record<ErpConnectionStatus, Level> = {
  draft: 'unknown',
  connected: 'ok',
  auth_failed: 'critical',
  unavailable: 'warning',
};

export const supportLabel: Record<ErpSupport, string> = {
  supported: 'Supported',
  limited: 'Limited',
  unverified: 'Not confirmed',
  manual: 'Done by hand in the ERP',
  unsupported: 'Not offered by this ERP',
};

export const supportLevel: Record<ErpSupport, Level> = {
  supported: 'ok',
  limited: 'warning',
  unverified: 'unknown',
  manual: 'unknown',
  unsupported: 'unknown',
};

export const operationLabel: Record<string, string> = {
  test_connection: 'Connection test',
  read_stock: 'Stock',
  read_prices: 'Prices',
  read_products: 'Product data (name, weight, VAT)',
  submit_order: 'Sending orders',
  submit_invoice: 'Sending invoices',
  submit_payment: 'Sending payments',
  submit_cancellation: 'Cancellations',
  submit_refund: 'Refunds',
  submit_return: 'Returns',
  submit_credit_note: 'Credit notes',
  fetch_documents: 'Fetching invoices and documents',
  verify_submission: 'Checking what was sent',
};

export const stockQuantityLabel: Record<ErpStockQuantity, string> = {
  on_hand: 'Quantity in the warehouse',
  available: 'Quantity not reserved by ERP orders',
};

export const stockQuantityHelp: Record<ErpStockQuantity, string> = {
  on_hand:
    'What the ERP says is physically in the warehouse. The store still subtracts its own unshipped orders.',
  available:
    'The warehouse quantity minus what the ERP’s own orders reserve. Use it when orders are also taken in the ERP.',
};

export const mappingStateLabel: Record<ErpMappingState, string> = {
  matched: 'Matched',
  no_code: 'No ERP code',
  unmatched: 'ERP code not found',
  conflict: 'In two websites',
};

/** What to do about a product that takes nothing from the ERP (CO-158). */
export const mappingNextStep: Partial<Record<ErpMappingState, string>> = {
  no_code: 'Set the product’s ERP code in the store.',
  unmatched: 'Check the code in the store, or add the article in the ERP.',
  conflict: 'Assign the product to one website only.',
};

export const mappingStateLevel: Record<ErpMappingState, Level> = {
  matched: 'ok',
  no_code: 'unknown',
  unmatched: 'warning',
  conflict: 'critical',
};

export const issueSeverityLabel: Record<ErpIssueSeverity, string> = {
  blocking: 'Blocks sending',
  warning: 'Worth a look',
  info: 'For information',
};

export const issueSeverityLevel: Record<ErpIssueSeverity, Level> = {
  blocking: 'critical',
  warning: 'warning',
  info: 'unknown',
};

export const issueCodeLabel: Record<string, string> = {
  erp_code_unknown: 'ERP codes the ERP does not report',
  mapping_conflict: 'ERP code used by two products',
  match_attribute_not_set: 'The store has not chosen a matching attribute',
  no_warehouse_mapped: 'The store maps no ERP warehouse',
  warehouse_not_reported: 'Mapped warehouse not reported by the ERP',
  erp_article_outside_mapping: 'Articles in no mapped warehouse',
  store_sku_matches_erp_code: 'SKUs equal to an ERP code, without the code set',
  erp_article_missing: 'Article no longer reported by the ERP',
  erp_negative_stock: 'Negative stock in the ERP',
  price_tax_treatment_not_confirmed: 'The store has not said whether ERP prices include VAT',
  erp_price_missing: 'Articles without a sale price',
  erp_price_record_unreadable: 'Article records the ERP would not give',
  erp_vat_rate_missing: 'Articles without a VAT rate',
  price_change_held: 'Price change held',
  store_webhook_failing: 'Updates are not reaching the store',
  store_product_in_several_websites: 'Products in two websites that take stock from the ERP',
  store_website_not_reported: 'Linked website no longer reported by the store',
};

/**
 * Who fixes an issue, and where (UX-19). "Usually": the split between an
 * agency and the merchant differs per shop, so this names where the fix is
 * made and who normally has access there, not a fact about this shop.
 */
export const issueFixedBy: Record<string, string> = {
  erp_code_unknown: 'Usually the merchant: correct the ERP code on the product in the store, or add the article in the ERP.',
  mapping_conflict: 'Usually the merchant: give each product its own ERP code in the store.',
  match_attribute_not_set: 'Whoever administers the store (often the agency): choose the matching attribute in the store’s ERP settings.',
  no_warehouse_mapped: 'Whoever administers the store (often the agency): map ERP warehouses in the store’s ERP settings.',
  warehouse_not_reported: 'The ERP’s administrator, or whoever keeps the store’s warehouse map.',
  erp_article_outside_mapping: 'Whoever administers the store (often the agency): map the warehouse these articles are in.',
  store_sku_matches_erp_code: 'Usually the merchant: set the ERP code on these products in the store.',
  erp_article_missing: 'Usually the merchant, in the ERP: the article was removed or renamed there.',
  erp_negative_stock: 'Usually the merchant, in the ERP: the stock record is below zero there.',
  price_tax_treatment_not_confirmed: 'Whoever administers the store (often the agency): say in the store’s ERP settings whether ERP prices include VAT.',
  erp_price_missing: 'Usually the merchant, in the ERP: give these articles a sale price.',
  erp_price_record_unreadable: 'Usually the merchant, in the ERP: the article was removed or renamed there. The price read earlier stays; Commerce Ops tries again in a few hours.',
  erp_vat_rate_missing: 'Usually the merchant, in the ERP: give these articles a VAT rate.',
  price_change_held: 'Anyone who manages ERP integrations here: accept the new price, or ignore it and correct it in the ERP.',
  store_webhook_failing: 'Usually the agency: the store’s connection to Commerce Ops is not accepting updates.',
  store_product_in_several_websites: 'Usually the merchant: assign these products to one of the websites in the store.',
  store_website_not_reported: 'Anyone who manages ERP integrations here: remove the link if the website was deleted in the store.',
};

/**
 * Where the products an issue names can be looked up (UX-19): store
 * products (SKU or the code set on them) in Product matching, ERP articles
 * in Inventory. Each tab's search matches those values.
 */
export const issueSampleTab: Record<string, 'matching' | 'inventory'> = {
  erp_code_unknown: 'matching',
  store_sku_matches_erp_code: 'matching',
  store_product_in_several_websites: 'matching',
  mapping_conflict: 'matching',
  price_change_held: 'matching',
  erp_article_outside_mapping: 'inventory',
  erp_price_missing: 'inventory',
  erp_price_record_unreadable: 'inventory',
  erp_vat_rate_missing: 'inventory',
  erp_article_missing: 'inventory',
};

export const runKindLabel: Record<string, string> = {
  stock: 'Stock read',
  test: 'Connection test',
};

export const runTriggerLabel: Record<string, string> = {
  schedule: 'Scheduled',
  manual: 'Started by a person',
};

export const runStateLabel: Record<string, string> = {
  running: 'Running',
  succeeded: 'Succeeded',
  failed: 'Failed',
};

export const failureClassLabel: Record<string, string> = {
  transient: 'Temporary',
  rate_limited: 'Rate limited',
  auth: 'Access key',
  permanent: 'Rejected',
  busy: 'Another call in progress',
  internal: 'Commerce Ops',
};

export const deliveryStateLabel: Record<string, string> = {
  pending: 'Waiting',
  leased: 'Sending',
  delivered: 'Delivered',
  dead: 'Given up',
};

export const deliveredViaLabel: Record<string, string> = {
  webhook: 'by webhook',
  pull: 'fetched by the store',
};

export const environmentTypeLabel: Record<string, string> = {
  production: 'Production',
  staging: 'Staging',
  development: 'Development',
  preview: 'Preview',
};

export { storeConnectionPath, storeSettingsPath } from './storeCopy';

/** The commerce platform a store extension runs on, as a person names it. */
export const storePlatformLabel: Record<string, string> = {
  magento2: 'Magento',
  woocommerce: 'WooCommerce',
};

export const priceUpdateModeLabel: Record<string, string> = {
  fill: 'Only fill empty prices',
  overwrite: 'Overwrite prices',
};

/**
 * storeSetupGaps lists what a store still has to set before Commerce Ops
 * can send it anything, in the order the store's settings show them.
 */
export function storeSetupGaps(erp: StoreErpSettings, words: StoreCopy = magentoStoreCopy): string[] {
  const gaps: string[] = [];
  if (!erp.enabled) return ['Turn on the ERP integration in the store'];
  if (!erp.match_attribute) gaps.push(`Choose the ${words.matchField} that holds the ERP article code`);
  if (!erp.locations || erp.locations.length === 0) {
    gaps.push(`Map at least one ERP warehouse to ${words.stockLocation === 'stock' ? 'the store’s stock' : `an ${words.stockLocation}`}`);
  }
  if (erp.price_sync && erp.erp_prices_include_tax == null) gaps.push('Answer whether the ERP’s prices include VAT');
  return gaps;
}

/** vatAnswerLabel shows the store's answer to "do the ERP's prices include VAT?". */
export function vatAnswerLabel(value: boolean | null | undefined): string {
  if (value == null) return 'Not answered: no prices are sent';
  return value ? 'Yes, they include VAT' : 'No, they exclude VAT';
}

export function labelOr(map: Record<string, string>, value: string | undefined): string {
  if (!value) return '—';
  return map[value] ?? value.replace(/_/g, ' ');
}

export function formatNumber(value: number): string {
  return new Intl.NumberFormat().format(value);
}

/** formatDecimal shows an API decimal string with the locale's separators. */
export function formatDecimal(value: string | undefined, fractionDigits?: number): string {
  if (value === undefined || value === '') return '—';
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return value;
  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: fractionDigits ?? 0,
    maximumFractionDigits: fractionDigits ?? 6,
  }).format(parsed);
}

/** untilWhen renders "in 4m" for the next scheduled read. */
export function untilWhen(value?: string): string {
  if (!value) return 'not scheduled';
  const parsed = new Date(value).getTime();
  if (Number.isNaN(parsed)) return 'not scheduled';
  const seconds = Math.round((parsed - Date.now()) / 1000);
  if (seconds <= 30) return 'due now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `in ${minutes}m`;
  return `in ${Math.round(minutes / 60)}h`;
}

/** One website of a store that takes stock from a connection (CO-134). */
export interface LinkedWebsite {
  /** Unique within a connection: the store and the website. */
  key: string;
  store: StoreInstallationView;
  website: StoreWebsiteView;
}

export function websiteKey(storeId: string, websiteCode: string): string {
  return `${storeId}:${websiteCode}`;
}

/**
 * linkedWebsites lists the websites that take stock from a connection, store
 * by store. Each website is listed once: a store that appears twice in
 * connection.stores must not double its websites (CO-154), and the entry key
 * identifies a website, so it is also what the lists render by.
 */
export function linkedWebsites(connection: ErpConnectionView): LinkedWebsite[] {
  const seen = new Set<string>();
  return connection.stores.flatMap((store) => {
    const websites = store.websites ?? [];
    // A store that reports named websites no longer has a whole-store ("")
    // website; one that is still listed is a leftover link.
    const named = websites.some((website) => website.code !== '');
    return websites
      .filter((website) => website.connection_id === connection.id && !(named && website.code === ''))
      .map((website) => ({ key: websiteKey(store.id, website.code), store, website }))
      .filter((entry) => {
        if (seen.has(entry.key)) return false;
        seen.add(entry.key);
        return true;
      });
  });
}

/**
 * websiteLabel names a website, or the store when it reports no websites
 * (code ""). The store connector's name is added only when several stores
 * take stock from the same connection (withStore): that name is the host
 * the extension was registered under, not the website's own domain, so
 * beside a single store it would only read as one (CO-158).
 */
export function websiteLabel(
  store: StoreInstallationView,
  website: Pick<StoreWebsiteView, 'code' | 'name'>,
  withStore = false,
): string {
  if (website.code === '') return store.name;
  const name = website.name || website.code;
  return withStore ? `${name} (${store.name})` : name;
}

/** Whether the websites of a connection come from more than one store. */
export function severalStores(linked: LinkedWebsite[]): boolean {
  return new Set(linked.map((entry) => entry.store.id)).size > 1;
}

/** One step before stock reaches a store, and the tab it is done on. */
export interface ErpSetupStep {
  done: boolean;
  label: string;
  tab: 'settings' | 'stores';
}

/**
 * erpSetupSteps lists what is left before stock reaches a store, in order.
 * Setup is a real sequence; every step must be done before anything is sent.
 */
export function erpSetupSteps(connection: ErpConnectionView, stores: StoreInstallationView[], words: StoreCopy = magentoStoreCopy): ErpSetupStep[] {
  // What the store set is per website (CO-134): every linked website must be set up.
  const reported = linkedWebsites(connection).filter((entry) => Boolean(entry.store.last_seen_at) && entry.website.reported);
  return [
    { done: connection.credential_configured, label: `Store the ${connection.provider_info.display_name} access key`, tab: 'settings' },
    { done: Boolean(connection.last_tested_at) && connection.status === 'connected', label: 'Test the connection', tab: 'settings' },
    { done: stores.length > 0 || connection.stores.length > 0, label: `Connect a store and paste its connection code into ${words.adminShort}`, tab: 'stores' },
    { done: reported.length > 0, label: 'Link the store to this connection and wait for the extension to report in', tab: 'stores' },
    {
      done: reported.length > 0 && reported.every((entry) => entry.website.erp.match_attribute !== ''),
      label: `In the store, choose the ${words.matchField} that holds the ERP article code`,
      tab: 'stores',
    },
    {
      done: reported.length > 0 && reported.every((entry) => (entry.website.erp.locations ?? []).length > 0),
      label:
        words.stockLocation === 'stock'
          ? 'In the store, map the ERP warehouses it sells from to its stock'
          : `In the store, map the ERP warehouses it sells from to its ${words.stockLocation}s`,
      tab: 'stores',
    },
  ];
}

/**
 * websiteDeliveryState says whether a website receives what Commerce Ops
 * sends, and if not, the first reason why. One wording for the data-path
 * diagram and the Stores table.
 */
export function websiteDeliveryState(
  store: StoreInstallationView,
  website: StoreWebsiteView,
  words: StoreCopy = magentoStoreCopy,
  /** The connection that serves the website: a stale or failed sync is not "up to date" (CO-184, IN1). */
  sync?: SyncHealthInput,
): { level: Level; label: string } {
  const erp = website.erp;
  const gaps = store.last_seen_at && erp.enabled ? storeSetupGaps(erp, words) : [];
  // Without the VAT answer only prices wait; stock is still sent.
  const vatOnly = erp.price_sync && erp.erp_prices_include_tax == null && gaps.length === 1;
  const firstGap = vatOnly ? '' : (gaps[0] ?? '');
  if (store.status !== 'active') return { level: 'unknown', label: 'Disabled in Commerce Ops' };
  if (!store.last_seen_at) return { level: 'warning', label: 'Extension not heard from yet' };
  if (!website.reported) return { level: 'warning', label: 'The store no longer reports this website' };
  if (store.deliveries.dead > 0) return { level: 'critical', label: 'Updates are not reaching it' };
  if (firstGap) return { level: 'warning', label: `Waiting for the store: ${firstGap.charAt(0).toLowerCase()}${firstGap.slice(1)}` };
  if (!erp.enabled || !erp.stock_sync) return { level: 'unknown', label: 'Stock sync is off in the store' };
  if (vatOnly) return { level: 'warning', label: 'Stock is sent; prices wait for the store’s VAT answer' };
  // Delivery is fine, but what is delivered is only as current as the last
  // sync: never "up to date" after a stale or failed one.
  const syncHealth = sync ? erpSyncHealth(sync) : null;
  if (syncHealth && syncHealth.kind !== 'ok') return { level: syncHealth.level, label: syncHealth.storeLabel };
  if (store.deliveries.pending > 0) {
    return { level: 'ok', label: `${store.deliveries.pending} ${store.deliveries.pending === 1 ? 'update' : 'updates'} on the way` };
  }
  return { level: 'ok', label: 'Up to date' };
}

/** The fields of a connection that say how its scheduled stock sync is doing. */
export type SyncHealthInput = Pick<
  ErpConnectionView,
  'stale' | 'last_stock_sync_status' | 'last_stock_sync_success_at' | 'consecutive_sync_failures'
>;

export type SyncHealthKind = 'ok' | 'stale' | 'failed' | 'never';

/**
 * erpSyncHealth is how the scheduled stock sync is doing, from what the
 * connection recorded. A website that waits on a failing sync is not "up to
 * date", and a connection that never synced has nothing to be up to date
 * with. A sync that is running now does not change the verdict of the last
 * one.
 */
export function erpSyncHealth(sync: SyncHealthInput): { kind: SyncHealthKind; level: Level; storeLabel: string } {
  if (sync.stale) return { kind: 'stale', level: 'critical', storeLabel: 'Stock is out of date' };
  if (sync.last_stock_sync_status === 'failed') {
    return { kind: 'failed', level: 'warning', storeLabel: 'Not refreshed: the last sync failed' };
  }
  if (!sync.last_stock_sync_success_at) return { kind: 'never', level: 'unknown', storeLabel: 'Waiting for the first sync' };
  return { kind: 'ok', level: 'ok', storeLabel: 'Up to date' };
}

/**
 * How recently a store's extension reported in, as a level. The extension is
 * expected to check in during a day (the same day the WooCommerce plugin is
 * given before Commerce Ops falls back to its webhooks); older than that is
 * not healthy, and "never" is the caller's own state.
 */
export function storeSeenLevel(lastSeenAt: string | undefined, now: number = Date.now()): Level {
  if (!lastSeenAt) return 'warning';
  const seen = new Date(lastSeenAt).getTime();
  if (Number.isNaN(seen)) return 'unknown';
  return now - seen < 24 * 60 * 60 * 1000 ? 'ok' : 'warning';
}

function hostOf(url: string | undefined): string {
  if (!url) return '';
  try {
    return new URL(url).host;
  } catch {
    return url.replace(/^https?:\/\//, '').replace(/\/.*$/, '');
  }
}

/**
 * publicDomainOf is the domain shoppers reach a website on (CO-158). A
 * headless website is served by the frontend bound to its website code; a
 * website Magento serves itself has its own domain, or its own base URL.
 * The store's reported base URL is where the extension talks to Commerce
 * Ops — on a headless store the shared Magento API host — so it is never
 * shown as a website's domain unless nothing else serves that website.
 * Null when not known.
 */
export function publicDomainOf(
  store: StoreInstallationView,
  website: Pick<StoreWebsiteView, 'code' | 'base_urls'>,
  components: ComponentView[] | null | undefined,
): string | null {
  const views = (components ?? []).filter((view) => !view.application.disabled);
  const frontends = views.filter((view) => view.application.type === 'frontend');
  if (website.code !== '') {
    const served = frontends.find((view) => (view.website_code ?? '') === website.code);
    if (served) {
      const canonical = served.domains.find((domain) => domain.canonical) ?? served.domains[0];
      const host = canonical?.hostname || hostOf(served.public_base_url);
      if (host) return host;
    }
    // A website Magento serves itself, on a domain bound to its code.
    const own = views
      .flatMap((view) => view.domains)
      .filter((domain) => domain.website_code === website.code && (domain.role === 'primary' || domain.role === 'frontend'))
      .sort((a, b) => Number(b.canonical) - Number(a.canonical))[0];
    if (own) return own.hostname;
  }
  const apiHost = hostOf(store.reported_base_urls[0]);
  const base = hostOf(website.base_urls?.[0]);
  // Without any frontend, Magento serves the website itself.
  if (frontends.length === 0) return base || apiHost || null;
  return base && base !== apiHost ? base : null;
}

// ---- Invoices and receipts (CO-147) -------------------------------------

export const documentModeLabel: Record<ErpDocumentMode, string> = {
  off: 'Off',
  preview: 'Preview only, send nothing',
  live: 'Send to the ERP automatically',
};

/**
 * What a document's state says in the connection's mode. A built document of
 * a connection that only previews is not "Ready": nothing will send it, and it
 * is never shown as healthy.
 */
export function documentStatusFor(state: ErpDocumentState, mode: ErpDocumentMode): { label: string; level: Level } {
  if (state === 'ready' && mode !== 'live') return { label: 'Built, not sent', level: 'unknown' };
  return { label: documentStateLabel[state], level: documentStateLevel[state] };
}

/** The sentence under a state, in the connection's mode (see documentStateNext). */
export function documentNextFor(state: ErpDocumentState, mode: ErpDocumentMode): string | undefined {
  if (state === 'ready' && mode !== 'live') return 'Not sent: preview only.';
  return documentStateNext[state];
}

// ---- What sending needs (readiness) -------------------------------------

/** The steps before sending, in the order a person does them. */
export const readinessLabel: Record<ErpReadinessId, string> = {
  key: 'Access key stored',
  test: 'Connection tested',
  company: 'Company confirmed',
  store_data: 'Store data on',
  store: 'Store connected',
  accounts: 'Payment accounts set',
  documents: 'Invoices checked',
  allowed: 'Sending allowed for this company',
  sending: 'Sending on',
};

/** Who acts, for a step that is not done. */
export const readinessWho: Partial<Record<ErpReadinessId, string>> = {
  key: 'Settings',
  store_data: 'The operator of this Commerce Ops',
  allowed: 'The operator of this Commerce Ops',
  store: 'Stores',
};

/** The steps that must be done before sending can be turned on; the server refuses otherwise. */
export const readinessRequired: ErpReadinessId[] = ['key', 'test', 'company', 'allowed'];

export function readinessStepOf(steps: ErpReadinessStep[] | undefined, id: ErpReadinessId): ErpReadinessStep | undefined {
  return steps?.find((step) => step.id === id);
}

/** Which required steps are not done. Steps the server did not send count as not done: unknown is never healthy. */
export function readinessMissing(steps: ErpReadinessStep[] | undefined): ErpReadinessId[] {
  return readinessRequired.filter((id) => readinessStepOf(steps, id)?.state !== 'done');
}

export const readinessStateWord: Record<ErpReadinessState, string> = {
  done: 'done',
  todo: 'not done yet',
  attention: 'needs attention',
  blocked: 'blocked',
  unknown: 'not known',
};

export const documentKindLabel: Record<ErpDocumentKind, string> = {
  invoice: 'Invoice',
  receipt: 'Receipt',
};

// Short: a badge in a narrow column must not wrap. The row's details say the rest.
export const documentStateLabel: Record<ErpDocumentState, string> = {
  blocked: 'Needs a fix',
  waiting: 'Waiting',
  ready: 'Ready',
  sent: 'Sent',
  failed: 'Retrying',
  refused: 'Refused',
  duplicate: 'Already there',
  skipped: 'Skipped',
  submitting: 'Sending',
  outcome_unknown: 'Outcome unknown',
};

export const documentStateLevel: Record<ErpDocumentState, Level> = {
  blocked: 'warning',
  waiting: 'unknown',
  ready: 'ok',
  sent: 'ok',
  failed: 'warning',
  refused: 'critical',
  duplicate: 'ok',
  skipped: 'unknown',
  submitting: 'unknown',
  // Not known to have failed, and not known to be there: never green.
  outcome_unknown: 'warning',
};

/** Previewed, staged and sent are three different things; a preview is none of the others. */
export const previewOnlyLabel = 'Preview only';
export const notStagedLabel = 'Not staged';

/** What a preview found, in the words a document's state uses. */
export const documentPreviewOutcomeLabel: Record<'ready' | 'blocked' | 'waiting' | 'skipped', string> = {
  ready: 'Can be built',
  blocked: documentStateLabel.blocked,
  waiting: documentStateLabel.waiting,
  skipped: 'Canceled in the store',
};

export const documentPreviewOutcomeLevel: Record<'ready' | 'blocked' | 'waiting' | 'skipped', Level> = {
  ready: 'ok',
  blocked: 'warning',
  waiting: 'unknown',
  skipped: 'unknown',
};

/** How a person settled an unknown outcome, as the send history shows it. */
export const attemptResolutionLabel: Record<'confirmed_sent' | 'not_sent', string> = {
  confirmed_sent: 'Confirmed sent',
  not_sent: 'Not in the ERP',
};

/**
 * What a person reads under a document's state, only where the state's label
 * leaves out what happens next or who acts. Sent, Already there and Skipped
 * say everything themselves (CO-184).
 */
export const documentStateNext: Partial<Record<ErpDocumentState, string>> = {
  blocked: 'Nothing is built until the problems below are fixed. It is checked again every few minutes.',
  waiting: 'The receipt is built once the store shows the order as paid.',
  ready: 'An invoice goes out on its own; a receipt waits for you to send it, unless receipts are sent automatically.',
  failed: 'The ERP did not take the last send. It is tried again on its own.',
  refused: 'The ERP read the file and said no. Read its report, fix the cause, then build again.',
  outcome_unknown: 'Not sent again until you decide.',
};

export interface DocumentFilterOption {
  value: string;
  label: string;
  /** Comma-separated states the API filters on. */
  states: string;
}

const allDocuments: DocumentFilterOption = { value: 'all', label: 'All', states: '' };

export const documentFilters: DocumentFilterOption[] = [
  allDocuments,
  { value: 'attention', label: 'Needs a fix', states: 'blocked,failed,refused,outcome_unknown' },
  { value: 'ready', label: 'Waiting to be sent', states: 'ready,submitting' },
  { value: 'waiting', label: 'Waiting for payment', states: 'waiting' },
  { value: 'done', label: 'Done', states: 'sent,duplicate' },
  { value: 'skipped', label: 'Skipped', states: 'skipped' },
];

/** A problem's code to a short title and who fixes it and how. Unknown codes show the server's own message. */
export const documentProblemTitle: Record<string, string> = {
  record_unreadable: 'The store’s record cannot be read',
  no_lines: 'The invoice has no lines',
  line_unmatched: 'A product has no ERP article',
  no_warehouse: 'No ERP warehouse for the website',
  shipping_article_missing: 'No shipping article',
  county_unknown: 'The county is not one the ERP knows',
  buyer_unnamed: 'The buyer has no name',
  buyer_place_missing: 'The buyer’s address is incomplete',
  vat_unexplained: 'The VAT does not add up',
  totals_differ: 'The totals do not add up',
  payment_method_not_mapped: 'No account for this payment method',
  receipt_number_too_long: 'The invoice number is too long for a receipt',
  invoice_number_missing: 'The invoice has no number',
  import_file_refused: 'The ERP would refuse this file',
};

export const documentProblemFix: Record<string, string> = {
  record_unreadable: 'Wait for the store to report it again; if it stays, the module on the store needs updating.',
  no_lines: 'Fix the invoice in the store, then rebuild.',
  line_unmatched: 'Match the product in Product matching. The invoice is built on its own afterwards.',
  no_warehouse: 'Choose the ERP warehouse for this website in Stores.',
  shipping_article_missing: 'Enter the ERP service article that shipping is invoiced under, in the settings above.',
  county_unknown: 'Correct the billing region in the store, then rebuild.',
  buyer_unnamed: 'Add a company or a name to the billing address in the store, then rebuild.',
  buyer_place_missing: 'Add the missing country or city to the billing address in the store, then rebuild.',
  vat_unexplained: 'Check the invoice in the store; the VAT must match its lines.',
  totals_differ: 'Check the invoice in the store; the lines must add up to its total.',
  payment_method_not_mapped: 'Choose the account this payment method’s money goes to, in the settings above.',
  receipt_number_too_long: 'The ERP allows 16 characters. Skip this receipt and enter it in the ERP by hand.',
  invoice_number_missing: 'Wait until the store numbers the invoice, then rebuild.',
};

export function documentFilterFor(value: string): DocumentFilterOption {
  return documentFilters.find((filter) => filter.value === value) ?? allDocuments;
}

/** The actions a state offers; the API refuses the rest. */
export function documentActions(state: ErpDocumentState): ErpDocumentAction[] {
  switch (state) {
    case 'ready':
    case 'failed':
    case 'refused':
      return ['send', 'rebuild', 'skip'];
    case 'blocked':
      return ['rebuild', 'skip'];
    case 'waiting':
      return ['skip'];
    case 'outcome_unknown':
      // Sending it is not offered: only a decision after looking in the ERP.
      return ['confirm_sent', 'resend', 'skip'];
    default:
      return [];
  }
}

export const documentActionLabel: Record<ErpDocumentAction, string> = {
  send: 'Send',
  rebuild: 'Build again',
  skip: 'Skip',
  confirm_sent: 'Confirm sent',
  resend: 'Send again',
};

/**
 * The count on a tab of the ERP section: Issues counts what is open, Invoices
 * the documents that need a person or are not moving (blocked, failing,
 * stuck, of unknown outcome). A server that sends no figure counts none.
 */
export function erpTabCount(tab: string, connection: Pick<ErpConnectionView, 'issues' | 'documents_attention'>): number {
  if (tab === 'issues') return connection.issues.blocking + connection.issues.warning;
  if (tab === 'documents') return connection.documents_attention?.documents ?? 0;
  return 0;
}

/** The two decisions about an unknown outcome name the ERP they are about. */
export function documentActionName(action: ErpDocumentAction, erpName: string): string {
  if (action === 'confirm_sent') return `Confirm sent in ${erpName}`;
  if (action === 'resend') return `Not in ${erpName}, send again`;
  return documentActionLabel[action];
}

export const documentActionDone: Record<ErpDocumentAction, string> = {
  send: 'Sent to the ERP.',
  rebuild: 'It will be built again within a few minutes.',
  skip: 'Skipped. It will not be sent.',
  confirm_sent: 'Confirmed as sent.',
  resend: 'It will be sent again within a few minutes.',
};

/** formatAmount shows an API decimal with a currency: "125.40 RON". */
export function formatAmount(value: string | undefined, currency: string): string {
  if (value === undefined || value === '') return '—';
  // A non-breaking space: an amount never splits from its currency.
  return `${formatDecimal(value, 2)}\u00A0${currency}`.trim();
}

/** formatDay shows a document's date in the reader's format; the API sends a day as midnight UTC. */
export function formatDay(iso: string | undefined): string {
  if (!iso) return '—';
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return '—';
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeZone: 'UTC' }).format(parsed);
}

/** A payment account is a class-5 ledger account: 5311, 5121, 5121.01. */
export const treasuryAccountPattern = /^5[0-9]{2,3}(\.[A-Za-z0-9]{1,12})?$/;

/**
 * Where sale prices come from (CO-185). The article's own record is read one
 * article at a time, so it is described as slow and bounded, never as live.
 */
export const priceSourceLabel: Record<ErpPriceSource, string> = {
  stock_report: 'The stock report',
  article_card: 'Each article’s own record',
};

export const priceSourceHelp: Record<ErpPriceSource, string> = {
  stock_report: 'The sale price field of the stock report, read with the stock. Some ERPs leave it out or at zero for every article.',
  article_card:
    'Read one article at a time, a few after each stock read with a pause between them, only for products a store matched and takes prices for. A price is refreshed at most once a day, and a large catalogue takes longer to come round; nothing is sent until it has been read.',
};
